import React, { useState, useEffect, useMemo } from "react";

import { useModal } from "../../../providers/ModalProvider/ModalProvider";

import { getBuyerTypes, updateOrderBuyerType } from "../../../utilities/api";

import Modal from "react-bootstrap/Modal";
import CloseButton from "react-bootstrap/CloseButton";
import Button from "react-bootstrap/Button";

import { Error } from "../Error";
import { Spinner } from "../../LoadingContainer/Spinner";

export default function UpdateBuyerType({
  order,
  organizationUUID,
  currentBuyerType,
  setOrders,
  onClose,
  onSuccess,
}) {
  const [buyerTypes, setBuyerTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);

  const { closeModal } = useModal();

  const currentId = currentBuyerType?.id ?? currentBuyerType;

  const categories = useMemo(() => {
    const packageItem = order?.package;
    const hasPackage = Boolean(packageItem);
    const tickets = hasPackage
      ? order?.packageTickets || []
      : order?.originalTickets || order?.tickets || [];
    const ticketCount = Array.isArray(tickets) ? tickets.length : 0;
    const primaryTicketType = ticketCount < 20 ? "individual" : "group";
    return [primaryTicketType];
  }, [order]);

  useEffect(() => {
    if (!organizationUUID) return;
    Promise.all(categories.map((cat) => getBuyerTypes(organizationUUID, cat)))
      .then((responses) => {
        const all = responses.flatMap((r) => r?.data?.data ?? r?.data ?? []);
        const byId = new Map();
        for (const bt of all) {
          const id = bt?.id ?? bt?.attributes?.id;
          if (id && !byId.has(id)) {
            byId.set(id, {
              id,
              name: bt?.attributes?.name ?? bt?.name,
              abbreviation: bt?.attributes?.abbreviation ?? bt?.abbreviation,
            });
          }
        }
        setBuyerTypes(Array.from(byId.values()));
      })
      .catch(() => setError(true))
      .finally(() => setIsLoading(false));
  }, [organizationUUID, categories]);

  const handleSelect = async (bt) => {
    setIsSaving(true);
    try {
      await updateOrderBuyerType({
        order,
        buyerTypeId: bt.id,
        organizationUUID,
      });
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id || o.orderId === order.orderId
            ? { ...o, buyer_type: bt }
            : o,
        ),
      );
      handleClose();
    } catch (err) {
      setError(true);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (onClose) onClose();
    closeModal();
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div className="text-center">
          <Spinner variant="dark" size="lg">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <div className="mt-3">Loading buyer types...</div>
        </div>
      </div>
    );
  }

  if (isSaving) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "200px" }}
      >
        <div className="text-center">
          <Spinner variant="dark" size="lg">
            <span className="visually-hidden">Updating buyer type...</span>
          </Spinner>
          <div className="mt-3">Updating buyer type...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ paddingTop: 32 }}>
        <Error
          title="An error has occurred. Please try again."
          description={null}
          onClose={() => {
            handleClose();
          }}
        />
      </div>
    );
  }

  return (
    <>
      <Modal.Header className="border-0 pb-0">
        <Modal.Title as="h4" className="fw-semibold">
          Update buyer type
        </Modal.Title>
        <CloseButton onClick={handleClose} />
      </Modal.Header>
      <Modal.Body>
        {buyerTypes.length === 0 ? (
          <div className="text-muted text-center py-3">
            No buyer types available for this organization.
          </div>
        ) : (
          <>
            <div className="d-flex flex-wrap gap-2">
              {buyerTypes.map((bt) => {
                const isSelected =
                  currentId != null &&
                  (bt.id === currentId || bt.id === Number(currentId));
                return (
                  <Button
                    key={bt.id}
                    variant="link"
                    className={`badge badge-sm badge-label badge-label-sm w-max-content text-body ${!isSelected ? "hover-effect" : ""}`}
                    onClick={() => !isSaving && handleSelect(bt)}
                    disabled={isSaving}
                  >
                    {bt.name || bt.abbreviation || "N/A"}
                    {isSelected && (
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 20 20"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                        className="ms-1"
                      >
                        <path
                          d="M16.6666 5L7.49992 14.1667L3.33325 10"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </Button>
                );
              })}
            </div>
          </>
        )}
      </Modal.Body>
    </>
  );
}
